import { useEffect, useState } from "react";
import { useRouterState } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { grantVipInvitation } from "@/lib/vip-invitation.functions";
import {
  clearVipInvitation,
  readVipInvitation,
  VIP_INVITATION_EVENT,
} from "@/lib/vip-invitation-intent";
import { Button } from "@/components/ui/button";

/** Resumes the invitation after login, OTP, confirmation or password recovery. */
export function VipInvitationResume() {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const [state, setState] = useState<"idle" | "saving" | "error">("idle");
  const [message, setMessage] = useState("");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    // Let the user finish choosing their new password before redirecting.
    if (pathname === "/reset-password") return;
    let disposed = false;
    let busy = false;
    let failed = false;
    async function resume() {
      if (busy || failed || disposed) return;
      const intent = readVipInvitation();
      if (!intent) return;
      busy = true;
      try {
        const { data } = await supabase.auth.getSession();
        if (
          disposed ||
          data.session?.user.email?.toLowerCase() !== intent.email
        )
          return;
        setState("saving");
        await grantVipInvitation();
        if (disposed) return;
        clearVipInvitation();
        // A fresh page also refreshes every existing role-dependent component.
        window.location.assign("/invitacion-vip?inicio=true#acceso-vip");
      } catch (error) {
        if (!disposed) {
          failed = true;
          setMessage(
            error instanceof Error
              ? error.message
              : "No se pudo activar el acceso VIP.",
          );
          setState("error");
        }
      } finally {
        busy = false;
      }
    }
    // Do not await Supabase methods from inside onAuthStateChange's lock.
    const schedule = () => {
      window.setTimeout(() => void resume(), 0);
    };
    schedule();
    const { data: listener } = supabase.auth.onAuthStateChange(schedule);
    window.addEventListener(VIP_INVITATION_EVENT, schedule);
    window.addEventListener("bizaclia:login-complete", schedule);
    return () => {
      disposed = true;
      listener.subscription.unsubscribe();
      window.removeEventListener(VIP_INVITATION_EVENT, schedule);
      window.removeEventListener("bizaclia:login-complete", schedule);
    };
  }, [attempt, pathname]);

  if (state === "idle") return null;
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-background/95 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="vip-activation-title"
    >
      <div
        className="w-full max-w-md rounded-2xl border bg-card p-8 text-center shadow-xl"
        aria-live="polite"
      >
        {state === "saving" && (
          <Loader2 className="mx-auto mb-4 h-8 w-8 animate-spin text-primary" />
        )}
        <h2 id="vip-activation-title" className="text-xl font-semibold">
          {state === "saving"
            ? "Activando tu acceso VIP…"
            : "No hemos podido activar tu acceso"}
        </h2>
        <p className="mt-3 text-muted-foreground">
          {state === "saving"
            ? "A continuación podrás elegir entre publicar o recibir activos off-market."
            : message}
        </p>
        {state === "error" && (
          <div className="mt-6 flex justify-center gap-3">
            <Button
              onClick={() => {
                setState("idle");
                setAttempt((value) => value + 1);
              }}
            >
              Reintentar
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                clearVipInvitation();
                setState("idle");
              }}
            >
              Cerrar
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
